import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {CrudFilterFieldTypes, CrudFilterOperator, ICrudFilter, ICrudSorting, ISource} from '../../sources/source';
import {useLocationSearch} from '../../utils/locationSearch';
import {BaseView, IBaseViewProps} from './BaseView';
import {Pagination} from './Pagination';
import {useSource} from './useSource';
import styles from './Grid.module.scss';
import {GridBaseRow, IGridColumn} from './GridBaseRow';

interface ITreeViewProps<ItemType, KeyFieldType>
  extends Pick<
    IBaseViewProps<ItemType, KeyFieldType>,
    'ErrorTemplate' | 'source' | 'PreloadTemplate'
  > {
  columns: IGridColumn<ItemType, KeyFieldType>[];
  pagination: {
    queryParamPage?: string;
    queryParamCountOnPage?: string;
    defaultCountPage: number;
    defaultPage?: number;
  };
  defaultFilter?: ICrudFilter<ItemType>[];
  defaultSorting?: ICrudSorting<ItemType>[];
  hierarchyField: keyof ItemType;
  root: any;
  hasChildrenField: keyof ItemType;
  header?: boolean;
  onRowClick?: (item:ItemType)=>void;
  headerTemplate?: (
    name: string,
    source: ISource<ItemType, KeyFieldType>,
  ) => JSX.Element;
}

interface ISubTreeViewProps<ItemType, KeyFieldType>
extends ITreeViewProps<ItemType, KeyFieldType> {
  deep: number;
  isExpanded: (key:string)=>boolean;
}

const SubTree = <ItemType, KeyFieldType>(props: ISubTreeViewProps<ItemType, KeyFieldType>) => {
  const subtreePagination = useMemo(()=>{
    return {page: 0, countOnPage: 50};
  }, []);
  const defaulyFilterPlusRoot = useMemo(()=>{
    const filterCombine = props.defaultFilter ? [...props.defaultFilter] : ([] as ICrudFilter<ItemType>[]);
    filterCombine.push(props.root ? {
      field: props.hierarchyField,
      type: CrudFilterFieldTypes.Field,
      value: props.root,
    } : {
      field: props.hierarchyField,
      type: CrudFilterFieldTypes.Field,
      value: null as any
    });
    return filterCombine;
  }, [props.defaultFilter, props.root])
  const [filter, setFilter] = useState(
    defaulyFilterPlusRoot,
  );
  useEffect(() => {
    if (defaulyFilterPlusRoot) setFilter(defaulyFilterPlusRoot);
  }, [defaulyFilterPlusRoot]);
  const [sorting, setSorting] = useState(
    props.defaultSorting || ([] as ICrudSorting<ItemType>[]),
  );
  useEffect(() => {
    if (props.defaultSorting) setSorting(props.defaultSorting);
  }, [props.defaultSorting]);
  const {list, hasData, error, meta} = useSource(
    props.source,
    filter,
    subtreePagination,
    sorting,
  );
  const RowTemplateWithColumns = useMemo(() => {
    return (_props: {
      item: ItemType;
      renderIndex: number;
      source: ISource<ItemType, KeyFieldType>;
    }) => {
      const isExpanded = props.isExpanded(_props.item[_props.source.keyField] as string);
      
      return <Fragment>
        <GridBaseRow {..._props} columns={props.columns}
          onRowClick={props.onRowClick} pad={(props.deep+1)*3}/>
        {_props.item[props.hasChildrenField] && isExpanded ?
        <SubTree {...props} root={_props.item[props.source.keyField]}
          deep={props.deep + 1}/> : null}
      </Fragment>;
    };
  }, [props.columns]);
  return (<BaseView
      list={list}
      hasData={hasData}
      error={error}
      {...props}
      RowTemplate={RowTemplateWithColumns}
    />
  );
};

export const TreeView = <ItemType, KeyFieldType>({
  pagination,
  defaultFilter,
  defaultSorting,
  header,
  onRowClick,
  ...props
}: ITreeViewProps<ItemType, KeyFieldType>) => {
  const {params} = useLocationSearch();
  const subtreePagination = useMemo(()=>{
    return {defaultCountPage: 50};
  }, []);
  const queryParamPage =
    pagination.queryParamPage && Number(params[pagination.queryParamPage]);
  const queryParamCountOnPage =
    pagination.queryParamCountOnPage &&
    Number(params[pagination.queryParamCountOnPage]);
  const [paginationState, setPaginationState] = useState({
    page: queryParamPage || pagination.defaultPage || 0,
    countOnPage: queryParamCountOnPage || pagination.defaultCountPage,
  });
  const defaulyFilterPlusRoot = useMemo(()=>{
    const filterCombine = defaultFilter ? [...defaultFilter] : ([] as ICrudFilter<ItemType>[]);
    filterCombine.push(props.root ? {
      field: props.hierarchyField,
      type: CrudFilterFieldTypes.Field,
      value: props.root,
    } : {
      field: props.hierarchyField,
      type: CrudFilterFieldTypes.Field,
      value: null as any
    });
    return filterCombine;
  }, [defaultFilter, props.root])
  const [filter, setFilter] = useState(
    defaulyFilterPlusRoot,
  );
  useEffect(() => {
    if (defaulyFilterPlusRoot) setFilter(defaulyFilterPlusRoot);
  }, [defaulyFilterPlusRoot]);
  const [sorting, setSorting] = useState(
    defaultSorting || ([] as ICrudSorting<ItemType>[]),
  );
  useEffect(() => {
    if (defaultSorting) setSorting(defaultSorting);
  }, [defaultSorting]);
  const {list, hasData, error, meta} = useSource(
    props.source,
    filter,
    paginationState,
    sorting,
  );
  useEffect(() => {
    let newPage = pagination.queryParamPage ? Number(queryParamPage) : -1;
    let newCountOnPage = pagination.queryParamCountOnPage
      ? Number(queryParamCountOnPage) || pagination.defaultCountPage
      : -1;
    setPaginationState((oldState) => {
      return {
        page: newPage > -1 ? newPage : oldState.page,
        countOnPage:
          newCountOnPage > -1 ? newCountOnPage : oldState.countOnPage,
      };
    });
  }, [queryParamPage, pagination, queryParamCountOnPage]);
  const _columns = props.columns;
  const expanded = useRef({} as any);
  const columns = useMemo(()=>{
    return [
      {
        name: 'exp',
        Template: (_props: {
          item: ItemType;
          name: string;
          renderRowIndex: number;
          source: ISource<ItemType, KeyFieldType>;
          onRowClick?: (item:ItemType)=>void;
        }) => {
          const key = _props.item[_props.source.keyField];
          const isExpanded = !!expanded.current[key];
          return <div className={_props.renderRowIndex % 2 ? styles.odd_row : ''}>
            {_props.item[props.hasChildrenField] ? <div onClick={()=>{
              expanded.current[key as string] = !isExpanded;
              _props.source.refresh();
            }}>
              {isExpanded ? 'collapse' : 'expand'}
            </div> : null}
          </div>
        }},
      ..._columns,
    ];
  }, [_columns, expanded]);
  const cssVars: any = useMemo(() => {
    return {
      '--columnCount': columns.length,
    };
  }, [columns]);
  const RowTemplateWithColumns = useMemo(() => {
    return (_props: {
      item: ItemType;
      renderIndex: number;
      source: ISource<ItemType, KeyFieldType>;
    }) => {
      const key = _props.item[_props.source.keyField];
      const isExpanded = !!expanded.current[key];
      return <Fragment>
        <GridBaseRow {..._props} columns={columns}
          onRowClick={onRowClick}/>
        {isExpanded && _props.item[props.hasChildrenField] ?
        <SubTree {...props} root={_props.item[props.source.keyField]}
          pagination={subtreePagination} deep={1} columns={columns}
          isExpanded={id=>!!expanded.current[id]}
          defaultFilter={defaultFilter} defaultSorting={defaultSorting}/> :
          null}
      </Fragment>;
    };
  }, [columns, expanded]);
  return (
    <div className={styles.root} style={cssVars}>
      <div className={styles.grid}>
        {header ? (
          <>
            {columns.map((column) => {
              return (
                <div key={column.name} className={styles.header}>
                  {props.headerTemplate
                    ? props.headerTemplate(column.name, props.source)
                    : column.name}
                </div>
              );
            })}
          </>
        ) : null}
        <BaseView
          list={list}
          hasData={hasData}
          error={error}
          {...props}
          RowTemplate={RowTemplateWithColumns}
        />
      </div>
      <Pagination
        page={paginationState.page}
        countOnPage={paginationState.countOnPage}
        hasNextPage={meta?.hasNextPage || false}
        queryParamPage={pagination.queryParamPage}
        onChangePage={(page) => {
          if (!pagination.queryParamPage) {
            setPaginationState((oldState) => {
              return {
                page: page,
                countOnPage: oldState.countOnPage,
              };
            });
          }
        }}
      />
    </div>
  );
};
